import React, { useState, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  Grid,
  Card,
  CardContent,
  CardActions,
  Button,
  Chip,
  IconButton,
  Menu,
  MenuItem,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Tabs,
  Tab,
  Badge,
  Avatar,
  Tooltip,
  useTheme,
  useMediaQuery,
  Skeleton,
  Alert,
  Snackbar,
  InputAdornment,
  FormControl,
  InputLabel,
  Select,
  Stack,
  Divider,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import {
  MoreVert as MoreIcon,
  Phone as PhoneIcon,
  Email as EmailIcon,
  LocationOn as LocationIcon,
  CalendarToday as CalendarIcon,
  AttachMoney as MoneyIcon,
  TrendingUp as TrendingUpIcon,
  CheckCircle as CheckIcon,
  Cancel as CancelIcon,
  Schedule as ScheduleIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  LocalShipping as TruckIcon,
  Chat as ChatIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Visibility as ViewIcon,
  FilterList as FilterIcon,
  Search as SearchIcon,
  Star as StarIcon,
  ExpandMore as ExpandMoreIcon,
} from '@mui/icons-material';
import { formatDistanceToNow, format } from 'date-fns';
import { apiFetch } from '../utils/apiFetch.js';

const EnhancedMoverBookings = ({ token }) => {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedTab, setSelectedTab] = useState(0);
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [messageBooking, setMessageBooking] = useState(null);
  const [viewMode, setViewMode] = useState('cards'); // 'cards' or 'table'
  const [searchQuery, setSearchQuery] = useState('');
  const [filterAnchorEl, setFilterAnchorEl] = useState(null);
  const [actionAnchorEl, setActionAnchorEl] = useState(null);
  const [actionBooking, setActionBooking] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'info' });
  
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));

  // Fetch real bookings from API
  useEffect(() => {
    const fetchBookings = async () => {
      setLoading(true);
      try {
        const headers = { 'Content-Type': 'application/json' };
        if (token && token !== 'cookie') headers['Authorization'] = `Bearer ${token}`;
        const res = await apiFetch('/api/bookings?mover=me', {
          headers,
        });
        if (!res.ok) throw new Error(`HTTP ${res.status}`);
        const data = await res.json();
        // Normalize fields for display
        const normalized = (Array.isArray(data) ? data : []).map(b => ({
          ...b,
          id: b._id || b.id,
          customerName: b.contactName || b.customerName || b.user?.name || 'Customer',
          customerPhone: b.contactPhone || b.customerPhone || b.phone || b.user?.phone || '',
          customerEmail: b.contactEmail || b.customerEmail || b.user?.email || '',
          moveDateValue: b.moveDate ? new Date(b.moveDate) : null,
          fromAddress: b.origin || b.fromAddress || '',
          toAddress: b.destination || b.toAddress || '',
          estimatedValue: b.amount || b.price || 0,
          serviceType: b.serviceType || b.moveType || 'Moving',
          crew: b.crew || [],
          lastUpdate: b.updatedAt || b.createdAt,
          priority: b.priority || 'medium',
        }));
        setBookings(normalized);
      } catch (err) {
        console.error('Error fetching bookings:', err);
        setSnackbar({ open: true, message: 'Failed to load bookings.', severity: 'error' });
        setBookings([]);
      } finally {
        setLoading(false);
      }
    };
    fetchBookings();
  }, [token]);

  const getStatusColor = (status) => {
    switch (status) {
      case 'confirmed':
        return 'success';
      case 'pending':
        return 'warning';
      case 'awaiting_customer_confirmation':
        return 'warning';
      case 'completed':
        return 'info';
      case 'cancelled':
        return 'error';
      default:
        return 'default';
    }
  };

  const getPriorityColor = (priority) => {
    switch (priority) {
      case 'high':
        return 'error';
      case 'medium':
        return 'warning';
      case 'low':
        return 'success';
      default:
        return 'default';
    }
  };

  const getStatusLabel = (status) => {
    if (status === 'awaiting_customer_confirmation') return 'Awaiting customer confirmation';
    return status || 'unknown';
  };

  const getTabCounts = () => {
    const all = bookings.length;
    const pending = bookings.filter(b => b.status === 'pending').length;
    const confirmed = bookings.filter(b => b.status === 'confirmed').length;
    const completed = bookings.filter(b => b.status === 'completed' || b.status === 'awaiting_customer_confirmation').length;
    
    return { all, pending, confirmed, completed };
  };

  const getFilteredBookings = () => {
    let filtered = [...bookings];
    
    // Filter by tab
    if (selectedTab === 1) filtered = filtered.filter(b => b.status === 'pending');
    if (selectedTab === 2) filtered = filtered.filter(b => b.status === 'confirmed');
    if (selectedTab === 3) filtered = filtered.filter(b => b.status === 'completed' || b.status === 'awaiting_customer_confirmation');
    
    // Filter by search
    if (searchQuery) {
      const query = searchQuery.toLowerCase();
      filtered = filtered.filter(b => 
        (b.customerName || '').toLowerCase().includes(query) ||
        (b.customerEmail || '').toLowerCase().includes(query) ||
        (b.jobNumber || '').toLowerCase().includes(query) ||
        (b.serviceType || '').toLowerCase().includes(query) ||
        (b.fromAddress || '').toLowerCase().includes(query) ||
        (b.toAddress || '').toLowerCase().includes(query)
      );
    }
    
    return filtered;
  };

  const handleActionClick = (booking, event) => {
    setActionBooking(booking);
    setActionAnchorEl(event.currentTarget);
  };

  const handleActionClose = () => {
    setActionAnchorEl(null);
    setActionBooking(null);
  };

  const handleStatusUpdate = async (bookingId, newStatus) => {
    try {
      const headers = { 'Content-Type': 'application/json' };
      if (token && token !== 'cookie') headers['Authorization'] = `Bearer ${token}`;
      const res = await apiFetch(`/api/bookings/${bookingId}/status`, {
        method: 'PUT',
        headers,
        body: JSON.stringify({ status: newStatus }),
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.error || `HTTP ${res.status}`);
      }
      const displayedStatus = newStatus === 'completed' ? 'awaiting_customer_confirmation' : newStatus;
      setBookings(prev => prev.map(b =>
        (b._id || b.id) === bookingId ? { ...b, status: displayedStatus, lastUpdate: new Date() } : b
      ));
      setSnackbar({ open: true, message: newStatus === 'completed' ? 'Customer confirmation requested.' : `Booking status updated to ${newStatus}`, severity: 'success' });
    } catch (err) {
      console.error('Status update error:', err);
      setSnackbar({ open: true, message: `Failed to update status: ${err.message}`, severity: 'error' });
    }
    handleActionClose();
  };

  const tabCounts = getTabCounts();
  const filteredBookings = getFilteredBookings();

  const getMailtoLink = (booking) => {
    const subject = encodeURIComponent(`MOVD booking ${booking?.jobNumber || booking?.id || ''}`.trim());
    const body = encodeURIComponent(`Hi ${booking?.customerName || 'there'},\n\nI'm reaching out about your MOVD booking ${booking?.jobNumber || ''}.\n\nThanks,\n${booking?.moverName || ''}`);
    return `mailto:${booking?.customerEmail || ''}?subject=${subject}&body=${body}`;
  };

  const renderBookingDetails = (booking) => (
    <>
      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <CalendarIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="body2">
              {booking.moveDateValue ? format(booking.moveDateValue, 'MMM dd, yyyy') : 'Date TBD'}
            </Typography>
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
            <PhoneIcon sx={{ fontSize: 16, color: 'text.secondary' }} />
            <Typography variant="body2">{booking.customerPhone || 'No phone provided'}</Typography>
          </Box>
        </Grid>
        <Grid item xs={12} sm={6}>
          <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, mb: 1 }}>
            <LocationIcon sx={{ fontSize: 16, color: 'text.secondary', mt: 0.5 }} />
            <Box sx={{ minWidth: 0 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>From:</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', overflowWrap: 'anywhere' }}>
                {booking.fromAddress || 'Pickup address TBD'}
              </Typography>
              <Typography variant="body2" sx={{ fontWeight: 500, mt: 0.5 }}>To:</Typography>
              <Typography variant="body2" color="text.secondary" sx={{ fontSize: '0.75rem', overflowWrap: 'anywhere' }}>
                {booking.toAddress || 'Dropoff address TBD'}
              </Typography>
            </Box>
          </Box>
        </Grid>
      </Grid>

      {booking.crew.length > 0 && (
        <Box sx={{ mt: 2 }}>
          <Typography variant="body2" sx={{ fontWeight: 500, mb: 1 }}>Assigned Crew:</Typography>
          <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
            {booking.crew.map((member, index) => (
              <Chip key={index} label={member} size="small" variant="outlined" avatar={<Avatar sx={{ width: 20, height: 20 }}>{member.charAt(0)}</Avatar>} />
            ))}
          </Box>
        </Box>
      )}

      {booking.notes && (
        <Alert severity="info" sx={{ mt: 2, fontSize: '0.875rem' }}>
          <strong>Note:</strong> {booking.notes}
        </Alert>
      )}
    </>
  );

  const renderBookingCard = (booking) => (
    <Card
      key={booking.id}
      sx={{
        mb: 2,
        borderRadius: 2,
        transition: 'all 0.2s ease-in-out',
        '&:hover': {
          transform: 'translateY(-2px)',
          boxShadow: 4,
        },
        border: booking.priority === 'high' ? 2 : 1,
        borderColor: booking.priority === 'high' ? 'error.main' : 'divider',
      }}
    >
      <CardContent sx={{ p: isMobile ? 2 : 3, pb: 1 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: isMobile ? 1.5 : 2, gap: 1 }}>
          <Box sx={{ flex: 1, minWidth: 0 }}>
            <Box sx={{ display: 'flex', alignItems: isMobile ? 'flex-start' : 'center', gap: 1, mb: 1, flexDirection: isMobile ? 'column' : 'row' }}>
              <Typography variant="h6" sx={{ fontWeight: 600, fontSize: isMobile ? '1rem' : '1.25rem' }}>
                {booking.customerName}
              </Typography>
              <Chip
                label={getStatusLabel(booking.status)}
                color={getStatusColor(booking.status)}
                size="small"
                sx={{ textTransform: 'capitalize' }}
              />
              {booking.priority === 'high' && !isMobile && (
                <Chip
                  label="High Priority"
                  color="error"
                  size="small"
                  variant="outlined"
                />
              )}
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
              {booking.serviceType} • ${booking.estimatedValue}
            </Typography>
            {booking.rating && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <StarIcon sx={{ fontSize: 16, color: 'warning.main' }} />
                <Typography variant="body2">{booking.rating}/5</Typography>
              </Box>
            )}
          </Box>
          
          <IconButton
            onClick={(e) => handleActionClick(booking, e)}
            size="small"
          >
            <MoreIcon />
          </IconButton>
        </Box>

        {isMobile ? (
          <Accordion disableGutters elevation={0} sx={{ mt: 1, border: '1px solid', borderColor: 'divider', borderRadius: 1.5, '&:before': { display: 'none' } }}>
            <AccordionSummary expandIcon={<ExpandMoreIcon />} sx={{ minHeight: 40, px: 1.5, '& .MuiAccordionSummary-content': { my: 1 } }}>
              <Typography variant="body2" fontWeight={700}>Move details</Typography>
            </AccordionSummary>
            <AccordionDetails sx={{ px: 1.5, pt: 0 }}>
              {renderBookingDetails(booking)}
            </AccordionDetails>
          </Accordion>
        ) : (
          renderBookingDetails(booking)
        )}
      </CardContent>

      <CardActions sx={{ px: isMobile ? 2 : 3, pb: 2, pt: 0, flexWrap: 'wrap', gap: 0.5 }}>
        <Button
          size="small"
          startIcon={<ChatIcon />}
          onClick={() => setMessageBooking(booking)}
        >
          Message
        </Button>
        <Button
          size="small"
          startIcon={<ViewIcon />}
          onClick={() => setSelectedBooking(booking)}
        >
          View Details
        </Button>
        {!["awaiting_customer_confirmation", "completed", "cancelled"].includes(booking.status) && (
          <Button
            size="small"
            color="success"
            variant="contained"
            startIcon={<CheckIcon />}
            onClick={() => handleStatusUpdate(booking._id || booking.id, 'completed')}
          >
            Complete Move
          </Button>
        )}
        <Box sx={{ flex: isMobile ? '0 0 100%' : 1 }} />
        <Typography variant="caption" color="text.secondary">
          Updated {formatDistanceToNow(booking.lastUpdate, { addSuffix: true })}
        </Typography>
      </CardActions>
    </Card>
  );

  if (loading) {
    return (
      <Box sx={{ p: 3 }}>
        <Skeleton variant="rectangular" width="100%" height={200} sx={{ mb: 2 }} />
        {[...Array(3)].map((_, i) => (
          <Skeleton key={i} variant="rectangular" width="100%" height={150} sx={{ mb: 2 }} />
        ))}
      </Box>
    );
  }

  return (
    <Box sx={{ p: { xs: 0, sm: 2, md: 3 } }}>
      {/* Header */}
      <Box sx={{ mb: isMobile ? 2 : 3 }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 700, mb: 1, fontSize: { xs: '1.75rem', md: '2.125rem' } }}>
              Bookings
            </Typography>
            <Typography variant="body1" color="text.secondary" sx={{ display: { xs: 'none', sm: 'block' } }}>
              Manage your moving bookings and customer requests
            </Typography>
          </Box>
        </Box>

        {/* Search and Filters */}
        <Paper sx={{ p: isMobile ? 1.5 : 2, mb: 2, borderRadius: 2 }}>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} sm={6} md={4}>
              <TextField
                fullWidth
                placeholder="Search bookings..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <SearchIcon />
                    </InputAdornment>
                  ),
                }}
                size="small"
              />
            </Grid>
            
            <Grid item xs={12} sm={6} md={4}>
              <Button
                variant="outlined"
                startIcon={<FilterIcon />}
                onClick={(e) => setFilterAnchorEl(e.currentTarget)}
                size="small"
              >
                Filters
              </Button>
            </Grid>
            
            {!isMobile && <Grid item xs={12} md={4}>
              <Box sx={{ display: 'flex', justifyContent: 'flex-end', gap: 1 }}>
                <Button
                  variant={viewMode === 'cards' ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => setViewMode('cards')}
                >
                  Cards
                </Button>
                <Button
                  variant={viewMode === 'table' ? 'contained' : 'outlined'}
                  size="small"
                  onClick={() => setViewMode('table')}
                >
                  Table
                </Button>
              </Box>
            </Grid>}
          </Grid>
        </Paper>
      </Box>

      {/* Tabs */}
      <Paper sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
        <Tabs
          value={selectedTab}
          onChange={(e, newValue) => setSelectedTab(newValue)}
          variant={isMobile ? 'scrollable' : 'fullWidth'}
          scrollButtons="auto"
        >
          <Tab
            label={
              <Badge badgeContent={tabCounts.all} color="primary" max={99}>
                All Bookings
              </Badge>
            }
          />
          <Tab
            label={
              <Badge badgeContent={tabCounts.pending} color="warning" max={99}>
                Pending
              </Badge>
            }
          />
          <Tab
            label={
              <Badge badgeContent={tabCounts.confirmed} color="success" max={99}>
                Confirmed
              </Badge>
            }
          />
          <Tab
            label={
              <Badge badgeContent={tabCounts.completed} color="info" max={99}>
                Completed
              </Badge>
            }
          />
        </Tabs>
      </Paper>

      {/* Content */}
      {filteredBookings.length === 0 ? (
        <Paper sx={{ p: 4, textAlign: 'center' }}>
          <BusinessIcon sx={{ fontSize: 64, color: 'text.disabled', mb: 2 }} />
          <Typography variant="h6" gutterBottom>
            {bookings.length === 0 ? 'No bookings yet' : 'No bookings match this view'}
          </Typography>
          <Typography variant="body2" color="text.secondary" paragraph>
            {searchQuery
              ? 'Try adjusting your search terms.'
              : bookings.length === 0
                ? 'New bookings will appear here once a customer completes the quote flow.'
                : 'Try switching to All Bookings or another status tab.'}
          </Typography>
        </Paper>
      ) : (
        <Box>
          {filteredBookings.map(booking => renderBookingCard(booking))}
        </Box>
      )}

      {/* Action Menu */}
      <Menu
        anchorEl={actionAnchorEl}
        open={Boolean(actionAnchorEl)}
        onClose={handleActionClose}
      >
        <MenuItem onClick={() => setSelectedBooking(actionBooking)}>
          <ViewIcon sx={{ mr: 1 }} />
          View Details
        </MenuItem>
        <MenuItem onClick={() => setMessageBooking(actionBooking)}>
          <ChatIcon sx={{ mr: 1 }} />
          Message Customer
        </MenuItem>
        {actionBooking?.moverAcceptanceRequired && actionBooking?.status === 'pending' && (
          <MenuItem onClick={() => handleStatusUpdate(actionBooking._id || actionBooking.id, 'confirmed')}>
            <CheckIcon sx={{ mr: 1 }} />
            Complete verification
          </MenuItem>
        )}
        <MenuItem onClick={() => handleStatusUpdate(actionBooking?._id || actionBooking?.id, 'completed')}>
          <CheckIcon sx={{ mr: 1 }} />
          Mark Complete
        </MenuItem>
        <Divider />
        <MenuItem onClick={() => handleStatusUpdate(actionBooking?._id || actionBooking?.id, 'cancelled')}>
          <CancelIcon sx={{ mr: 1 }} />
          Cancel Booking
        </MenuItem>
      </Menu>

      <Dialog open={!!selectedBooking} onClose={() => setSelectedBooking(null)} fullWidth maxWidth="sm">
        <DialogTitle>Booking Details</DialogTitle>
        {selectedBooking && (
          <DialogContent dividers>
            <Stack spacing={2}>
              <Box>
                <Typography variant="h6" fontWeight={700}>{selectedBooking.customerName}</Typography>
                <Typography variant="body2" color="text.secondary">Job #{selectedBooking.jobNumber || selectedBooking.id}</Typography>
              </Box>
              <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
                <Chip label={getStatusLabel(selectedBooking.status)} color={getStatusColor(selectedBooking.status)} size="small" />
                <Chip label={`$${Number(selectedBooking.estimatedValue || 0).toLocaleString()}`} color="primary" size="small" variant="outlined" />
              </Box>
              <Divider />
              {renderBookingDetails(selectedBooking)}
              {selectedBooking.customerEmail && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                  <EmailIcon sx={{ fontSize: 18, color: 'text.secondary' }} />
                  <Typography variant="body2" sx={{ overflowWrap: 'anywhere' }}>{selectedBooking.customerEmail}</Typography>
                </Box>
              )}
            </Stack>
          </DialogContent>
        )}
        <DialogActions>
          {selectedBooking?.customerEmail && <Button href={getMailtoLink(selectedBooking)}>Email Customer</Button>}
          {selectedBooking?.customerPhone && <Button href={`tel:${selectedBooking.customerPhone}`}>Call</Button>}
          <Button onClick={() => setSelectedBooking(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={!!messageBooking} onClose={() => setMessageBooking(null)} fullWidth maxWidth="sm">
        <DialogTitle>Message Customer</DialogTitle>
        {messageBooking && (
          <DialogContent dividers>
            <Stack spacing={2}>
              <Typography variant="body2" color="text.secondary">
                Send a message using the customer's email or phone for booking {messageBooking.jobNumber || messageBooking.id}.
              </Typography>
              <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
                <Typography variant="subtitle2" fontWeight={700}>{messageBooking.customerName}</Typography>
                <Typography variant="body2" color="text.secondary" sx={{ overflowWrap: 'anywhere' }}>{messageBooking.customerEmail || 'No email provided'}</Typography>
                <Typography variant="body2" color="text.secondary">{messageBooking.customerPhone || 'No phone provided'}</Typography>
              </Paper>
            </Stack>
          </DialogContent>
        )}
        <DialogActions>
          {messageBooking?.customerEmail && <Button href={getMailtoLink(messageBooking)} startIcon={<EmailIcon />}>Open Email</Button>}
          {messageBooking?.customerPhone && <Button href={`tel:${messageBooking.customerPhone}`} startIcon={<PhoneIcon />}>Call</Button>}
          <Button onClick={() => setMessageBooking(null)}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default EnhancedMoverBookings;